<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import { getLanguageDirection } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import { setI18nLocale } from 'dashboard/i18n/instance';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccountId from './components/AccountId.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
// import AutoResolve from './components/AutoResolve.vue';
import AudioTranscription from './components/AudioTranscription.vue';
import SectionLayout from './components/SectionLayout.vue';
import AutoResolve from './components/AutoResolveCustom.vue';
import GoogleSheetsConfig from './components/GoogleSheetsConfig.vue';
import ElevenLabsApiKeyConfig from './components/ElevenLabsApiKeyConfig.vue';
import FirecrawlApiKeyConfig from './components/FirecrawlApiKeyConfig.vue';

export default {
  components: {
    PageLayout,
    PageHeader,
    NextButton,
    AccountId,
    BuildInfo,
    AccountDelete,
    AutoResolve,
    AudioTranscription,
    SectionLayout,
    WithLabel,
    NextInput,
    GoogleSheetsConfig,
    ElevenLabsApiKeyConfig,
    FirecrawlApiKeyConfig,
  },
  setup() {
    const { updateUISettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      features: {},
      // Tab ativa na sessão de Integrações. Persiste em memória só dentro
      // da página — não tem URL sync porque cada tab é uma sub-config
      // independente, sharable como /settings/general (não /sheets).
      // OpenAI/IA saíram daqui — agora moram no Studio > Credenciais de IA.
      activeIntegrationTab: 'sheets',
      // Tab principal da pagina (padrao igual /settings/inboxes/:id):
      // Geral / Automacao / Integracoes / Conta.
      activeTab: 'general',
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    showAutoResolutionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
      );
    },
    showAudioTranscriptionConfig() {
      /*
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.CAPTAIN
      );
      */

      return true;
    },
    languagesSortedByCode() {
      // O projeto só mantém traduções de `en` e `pt_BR` (ver i18n/locale/).
      // Oferecer outras variantes (ex: 'pt' genérico) gera fallback e chaves
      // i18n faltando, então o dropdown é restrito aos locales suportados.
      const supportedLocales = ['en', 'pt_BR'];
      return [...this.enabledLanguages]
        .filter(lang => supportedLocales.includes(lang.iso_639_1_code))
        .sort((l1, l2) => l1.iso_639_1_code.localeCompare(l2.iso_639_1_code));
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },
    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    integrationTabs() {
      return [
        {
          id: 'sheets',
          label: 'Google Sheets',
          icon: 'i-lucide-table-2',
        },
        { id: 'elevenlabs', label: 'ElevenLabs', icon: 'i-lucide-mic' },
        { id: 'firecrawl', label: 'Firecrawl', icon: 'i-lucide-globe' },
      ];
    },
    mainTabs() {
      return [
        { key: 'general', name: 'Geral', icon: 'i-lucide-settings' },
        { key: 'automation', name: 'Automação', icon: 'i-lucide-workflow' },
        { key: 'integrations', name: 'Integrações', icon: 'i-lucide-plug' },
        { key: 'account', name: 'Conta', icon: 'i-lucide-info' },
      ];
    },
    activeTabIndex() {
      return this.mainTabs.findIndex(t => t.key === this.activeTab);
    },
  },
  mounted() {
    this.initializeAccount();
  },
  methods: {
    onTabChange(index) {
      this.activeTab = this.mainTabs[index].key;
    },
    async initializeAccount() {
      try {
        const { name, locale, id, domain, support_email, features } =
          this.getAccount(this.accountId);

        await setI18nLocale(locale);
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.features = features;
      } catch (error) {
        // Ignore error
      }
    },

    async updateAccount() {
      // Valida só os campos deste form (name, locale). O `useVuelidate()` sem
      // args agrega validações de componentes-filhos montados — ex: validações
      // de cards de integração montados via v-show em outra tab — e não devem
      // bloquear o salvamento das configurações gerais.
      this.v$.name.$touch();
      this.v$.locale.$touch();
      if (this.v$.name.$invalid || this.v$.locale.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
        });
        await setI18nLocale(this.locale);
        this.getAccount(this.id).locale = this.locale;
        this.updateDirectionView(this.locale);
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    updateDirectionView(locale) {
      const isRTLSupported = getLanguageDirection(locale);
      this.updateUISettings({
        rtl_view: isRTLSupported,
      });
    },
  },
};
</script>

<template>
  <!-- Layout padrao via <PageLayout> + <PageHeader>: header full-width
       com tile accent-aware (i-lucide-settings) + body scrollavel com
       padding p-6. -->
  <PageLayout>
    <template #header>
      <PageHeader
        icon="i-lucide-settings"
        accent="blue"
        :title="$t('GENERAL_SETTINGS.TITLE')"
        :subtitle="$t('GENERAL_SETTINGS.SUBTITLE')"
      />
      <!-- Tabs principais — agrupam as varias secoes da pagina (padrao
           ClickUp, igual ao /settings/inboxes/:id). -->
      <div class="px-6">
        <woot-tabs :index="activeTabIndex" border @change="onTabChange">
          <woot-tabs-item
            v-for="(tab, index) in mainTabs"
            :key="tab.key"
            :index="index"
            :name="tab.name"
            :icon="tab.icon"
            :show-badge="false"
            is-compact
          />
        </woot-tabs>
      </div>
    </template>
    <SectionLayout
      v-show="activeTab === 'general'"
      :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
      :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
    >
      <form
        v-if="!uiFlags.isFetchingItem"
        class="grid gap-4"
        @submit.prevent="updateAccount"
      >
        <WithLabel
          :has-error="v$.name.$error"
          :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
          :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
        >
          <NextInput
            v-model="name"
            type="text"
            class="w-full"
            :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </WithLabel>
        <WithLabel
          :has-error="v$.locale.$error"
          :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
          :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
        >
          <select v-model="locale" class="!mb-0 text-sm">
            <option
              v-for="lang in languagesSortedByCode"
              :key="lang.iso_639_1_code"
              :value="lang.iso_639_1_code"
            >
              {{ lang.name }}
            </option>
          </select>
        </WithLabel>
        <WithLabel
          v-if="featureCustomReplyDomainEnabled"
          :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
        >
          <NextInput
            v-model="domain"
            type="text"
            class="w-full"
            :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
          />
          <template #help>
            {{
              featureInboundEmailEnabled &&
              $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
            }}

            {{
              featureCustomReplyDomainEnabled &&
              $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
            }}
          </template>
        </WithLabel>
        <WithLabel
          v-if="featureCustomReplyEmailEnabled"
          :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
        >
          <NextInput
            v-model="supportEmail"
            type="text"
            class="w-full"
            :placeholder="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')"
          />
        </WithLabel>
        <div>
          <NextButton blue :is-loading="isUpdating" type="submit">
            {{ $t('GENERAL_SETTINGS.SUBMIT') }}
          </NextButton>
        </div>
      </form>
    </SectionLayout>

    <woot-loading-state v-if="uiFlags.isFetchingItem" />

    <div v-show="activeTab === 'automation'">
      <AutoResolve v-if="showAutoResolutionConfig" />
      <AudioTranscription v-if="showAudioTranscriptionConfig" />
    </div>

    <!-- Integrações: agrupadas em tabs pra não empilhar 4 cartões iguais
         na vertical. Cada tab carrega só seu componente — store action
         de fetch roda no onMounted do filho. -->
    <section
      v-show="activeTab === 'integrations'"
      class="border-t border-n-weak pt-8 pb-8"
    >
      <header class="mb-5">
        <h4 class="text-lg font-medium text-n-slate-12">
          {{ $t('GENERAL_SETTINGS.INTEGRATIONS.TITLE') }}
        </h4>
        <p class="text-n-slate-11 text-sm mt-2">
          {{ $t('GENERAL_SETTINGS.INTEGRATIONS.DESCRIPTION') }}
        </p>
      </header>

      <div class="flex gap-1 mb-4 border-b border-n-weak">
        <button
          v-for="tab in integrationTabs"
          :key="tab.id"
          type="button"
          class="flex items-center gap-1.5 px-3 py-2 text-sm transition-colors border-b-2 -mb-px"
          :class="
            activeIntegrationTab === tab.id
              ? 'text-n-blue-11 border-n-blue-9'
              : 'text-n-slate-10 border-transparent hover:text-n-slate-12'
          "
          @click="activeIntegrationTab = tab.id"
        >
          <span class="size-4" :class="[tab.icon]" />
          {{ tab.label }}
        </button>
      </div>

      <GoogleSheetsConfig v-if="activeIntegrationTab === 'sheets'" />
      <ElevenLabsApiKeyConfig
        v-else-if="activeIntegrationTab === 'elevenlabs'"
      />
      <FirecrawlApiKeyConfig v-else-if="activeIntegrationTab === 'firecrawl'" />
    </section>

    <div v-show="activeTab === 'account'">
      <AccountId />
      <div v-if="!uiFlags.isFetchingItem && isOnChatwootCloud">
        <AccountDelete />
      </div>
      <BuildInfo />
    </div>
  </PageLayout>
</template>
